import { z } from "zod";

const facetImageDataSchema = z.object({
  image: z
    .object({
      url: z.string(),
    })
    .optional(),
  description: z.string(),
});

type FacetImageData = z.infer<typeof facetImageDataSchema>;

/**
 * Try to parse a string as JSON and validate it against our MM color swatch schema
 */
const tryParseSwatchImageFacetData = (
  str: string,
): FacetImageData | undefined => {
  try {
    const parsed = JSON.parse(str);
    const result = facetImageDataSchema.safeParse(parsed);
    return result.success ? result.data : undefined;
  } catch (e) {
    return undefined;
  }
};

/**
 * LRU cache with size limit to prevent unbounded memory growth
 * Safe for SSR as it auto-cleans and has a small fixed size
 */
const colorCache = new Map<string, string>();
const MAX_CACHE_SIZE = 50; // Small limit to prevent memory issues

/**
 * Simple LRU eviction - removes oldest entries when cache is full
 */
const evictOldEntries = () => {
  if (colorCache.size >= MAX_CACHE_SIZE) {
    const entriesToRemove = colorCache.size - MAX_CACHE_SIZE + 10; // Remove 10 at a time
    const keysToDelete = Array.from(colorCache.keys()).slice(
      0,
      entriesToRemove,
    );
    keysToDelete.forEach((key) => colorCache.delete(key));
  }
};

/**
 * Extracts clean color description from JSON value for use as URL-friendly ID
 */
export const registerColorFacet = (jsonValue: string): string => {
  const parsed = tryParseSwatchImageFacetData(jsonValue);
  if (parsed) {
    const description = parsed.description;

    evictOldEntries();
    colorCache.set(description, jsonValue);

    return description;
  }
  return jsonValue;
};

/**
 * Resolves color description back to full JSON for Algolia queries
 *
 * Important: This uses a cache that's populated as facets load.
 * For direct URL navigation (bookmarked links), the first query might use
 * the description directly. Subsequent queries will use the cached full JSON.
 *
 * This is acceptable because:
 * 1. Algolia's disjunctive faceting will still return all color facets
 * 2. The cache gets populated immediately from the first response
 * 3. User interaction filters work perfectly after initial load
 */
export const resolveColorFacetValue = (description: string): string => {
  return colorCache.get(description) || description;
};
